import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import InfoBar from '../../components/Navigation/InfoBar';
import useApi from '../../hooks/use-api';
import {
	dationInstanceUserList,
	getCompanyContactPersons,
	inviteDationInstanceUserToPortal,
	inviteContactPersonToPortal,
	updateDationInstanceUser,
	deleteCompanyContactPerson,
	getSendInviteUrl,
	EDIT_USER,
} from '../../constants/routes';
import * as ROUTES from '../../constants/routes';
import { useUser } from '../../hooks/use-user';
import CompanyContactPersonOverview from '../../components/Tables/Users/CompanyContactPersonUser/CompanyContactPersonOverview';
import InviteUserModal from '../../components/Modal/InviteUserModal';
import DeleteUserModal from '../DeleteUserModal';
import { getDisplayName } from '../../utils/person';
import DationInstanceUserOverview from '../../components/Tables/Users/DationInstanceUser/DationInstanceUserOverview';

function UsersListPage() {
	const { t } = useTranslation();
	const { profile, activeCompany, isCompanyAdmin, isCompanySuperUser } = useUser();
	const navigate = useNavigate();

	const [showInviteModal, setShowInviteModal] = useState(false);
	const [showDeleteModal, setDeleteModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const [users, setUsers] = useState({
		data: null,
		company: {},
		dationInstance: {},
	});
	const [{ data: dationInstanceUsers }, fetchDationInstanceUsers] = useApi.get(
		dationInstanceUserList(profile?.dationInstance?.id),
		{},
		{ lazy: true },
	);
	const [{ data: contactPersonUsers }, fetchContactPersonUsers] = useApi.get(
		getCompanyContactPersons(activeCompany?.id),
		{},
		{ lazy: true },
	);

	useEffect(() => {
		const fetchUsers =
			isCompanySuperUser || isCompanyAdmin ? fetchContactPersonUsers : fetchDationInstanceUsers;
		fetchUsers();
	}, [isCompanySuperUser, isCompanyAdmin]);

	useEffect(() => {
		if (contactPersonUsers && contactPersonUsers.length > 0) {
			setUsers({
				data: contactPersonUsers,
				fetchData: fetchContactPersonUsers,
				inviteEndpoint: inviteContactPersonToPortal,
				newInviteEndpoint: getSendInviteUrl,
				deleteEndpoint: deleteCompanyContactPerson,
				company: {
					id: activeCompany.id,
				},
			});
		}
	}, [contactPersonUsers]);

	useEffect(() => {
		if (dationInstanceUsers && dationInstanceUsers.length > 0) {
			setUsers({
				data: dationInstanceUsers,
				fetchData: fetchDationInstanceUsers,
				inviteEndpoint: inviteDationInstanceUserToPortal,
				deleteEndpoint: updateDationInstanceUser,
				dationInstance: profile.dationInstance,
			});
		}
	}, [dationInstanceUsers]);

	const endpoint = users.inviteEndpoint ? users.inviteEndpoint(selectedUser?.id) : null;
	const newInviteRoute = users.newInviteEndpoint ? users.newInviteEndpoint(selectedUser?.id) : null;
	const { companyContactPersonUser = null } = selectedUser || {};
	const listType = isCompanySuperUser || isCompanyAdmin ? 'contact_person' : 'dation_instance_user';
	const userName = getDisplayName(selectedUser || {});
	const userTypeLang = t(`users.type.${listType}`);
	const company = isCompanySuperUser || isCompanyAdmin ? activeCompany : null;

	return (
		<>
			<InfoBar
				backTitle={
					isCompanySuperUser || isCompanyAdmin
						? t('add_employee.contact_persons')
						: t('users.title')
				}
				goBack={() => navigate(ROUTES.SETTINGS)}
			/>
			<InviteUserModal
				show={showInviteModal}
				setShow={setShowInviteModal}
				endpoint={companyContactPersonUser ? newInviteRoute : endpoint}
				handleSubmit={users.fetchData}
				title={t('users.send_invite')}
				message={t('users.confirm_invite', { emailAddress: selectedUser?.emailAddress })}
				successMessage={t('users.invite_send', { userType: userTypeLang, name: userName })}
			/>
			<DeleteUserModal
				deleteEndpoint={
					users.deleteEndpoint ? users.deleteEndpoint(selectedUser?.id, company?.id) : null
				}
				show={showDeleteModal}
				setShow={(show) => {
					setSelectedUser({});
					setDeleteModal(show);
				}}
				title={t('users.delete')}
				message={t('users.confirm_delete', { userType: userTypeLang, name: userName })}
				handleSubmit={users.fetchData}
				successMessage={t('users.deleted', { userType: userTypeLang, name: userName })}
			/>
			{users.data === null ? (
				<div className="loading-spinner" />
			) : (
				<>
					<div className="d-flex flex-row-reverse">
						<Button
							variant="outline-secondary"
							className="rounded-button p-1"
							type="button"
							onClick={() =>
								navigate(EDIT_USER, {
									state: {
										userType:
											isCompanySuperUser || isCompanyAdmin
												? 'contact_person'
												: 'dation_instance_user',
										original: {},
										company: activeCompany,
										dationInstance: { id: profile?.dationInstance?.id },
									},
								})
							}>
							<span className="glyphicons glyphicons-user-plus" />
						</Button>
					</div>
					<div className="col-12 col-xl-10 mx-auto pt-n5">
						{listType === 'contact_person' ? (
							<CompanyContactPersonOverview
								users={users.data}
								handleDelete={(original) => {
									setSelectedUser(original);
									setDeleteModal(true);
								}}
								handleRowClick={(original) => {
									navigate(EDIT_USER, {
										state: {
											userType: 'contact_person',
											original,
											company: activeCompany,
										},
									});
								}}
								handleInvite={(original) => {
									setSelectedUser(original);
									setShowInviteModal(true);
								}}
							/>
						) : (
							<DationInstanceUserOverview
								users={users.data}
								handleDelete={(original) => {
									setSelectedUser(original);
									setDeleteModal(true);
								}}
								handleInvite={(original) => {
									setSelectedUser(original);
									setShowInviteModal(true);
								}}
								handleRowClick={(original) =>
									navigate(EDIT_USER, {
										state: {
											userType: 'dation_instance_user',
											original,
											company: undefined,
											dationInstance: original.dationInstance,
										},
									})
								}
							/>
						)}
					</div>
				</>
			)}
		</>
	);
}

export default UsersListPage;
